import './verification-otp.scss'

import classnames from 'classnames'
import React, { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import Header from '../../components/collections/Header'
import { CoverSpinning } from '../../components/elements'
import Countdown from '../../components/elements/Countdown'
import Modal from '../../components/modals/Modal'
import { NoticeMessage } from '../../constants'
import TagManager from '../../lib/tag-manager'
import { ActionType } from '../../providers/action'
import { useAppState, useDispatcher } from '../../providers/store'
import service from '../../services'
import Strings from '../../utils/Strings'

class SocialProfileException extends Error {
    constructor() {
        super()
        this.name = 'social_profile_exception'
        this.message = 'SocialProfileException'
    }
}

export interface VerificationOTPForms {
    otp: string
}

interface NoticeModalProps {
    title: string
    description: string
    onClickButton: () => void
}

function NoticeModal({ title, description, onClickButton }: NoticeModalProps) {
    return (
        <Modal>
            <div className="notice-modal-content abcs-container">
                <h4>{title}</h4>
                <p>{description}</p>
                <button
                    type="button"
                    className="abcs-button secondary fluid"
                    onClick={onClickButton}
                    style={{ color: 'white' }}
                >
                    ปิด
                </button>
            </div>
        </Modal>
    )
}

export default function VerificationOTP() {
    const [loading, setLoading] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [showTooManyResentModal, setShowTooManyResentModal] = useState(false)
    const [updatingProfileModal, setUpdatingProfileModal] = useState(false)
    const [hasExpired, setHasExpired] = useState(false)
    const navigate = useNavigate()
    const appState = useAppState()
    const dispatch = useDispatcher()
    const { otpService, socialService } = service()
    const {
        register,
        handleSubmit,
        formState: { errors },
        trigger,
        reset,
        clearErrors,
    } = useForm<VerificationOTPForms>()
    const otpField = register('otp', {
        required: {
            value: true,
            message: 'กรุณาระบุ OTP ที่ได้จาก SMS',
        },
    })

    useMemo(() => {
        TagManager.loadEvent({ page: 'verification_phone_otp' })
    }, [])

    const isKYCForCampaign = appState.kycContext === 'campaign'

    const validate = async () => {
        await trigger()
    }

    const onClickResentOTP = async () => {
        if (!hasExpired) return
        try {
            setLoading(true)
            if (!appState.phoneNumber) throw Error('phone number required')
            setHasExpired(false)
            const { userId } = await window.liff.getProfile()
            const result = await otpService.requestOTPByCID(appState.cid ?? '', userId)
            dispatch({ type: ActionType.SAVED_REFCODE, payload: result })
            setLoading(false)
        } catch {
            setLoading(false)
            setShowTooManyResentModal(true)
            TagManager.popupEvent({ page: 'verification_phone_otp', field: 'too_many_resent_otp' })
        }
    }

    const updateMappingProfile = async () => {
        const lineProfile = await window.liff.getProfile()
        await socialService.save({
            avatarUrl: lineProfile.pictureUrl || '',
            cid: appState.cid,
            language: 'TH',
            osPlatform: window.liff.getOS()?.toUpperCase() as any,
            socialPlatform: 'Line',
            socialPlatformId: lineProfile.userId,
        })
    }

    const onSubmitSentOTP = async (data: VerificationOTPForms) => {
        setLoading(true)
        try {
            await otpService.verifyOTP(data.otp, appState.reference ?? '')

            try {
                await updateMappingProfile()
                setLoading(false)
                navigate('/kyc/done')
            } catch {
                throw new SocialProfileException()
            }
        } catch (error) {
            setLoading(false)
            if (error instanceof SocialProfileException) {
                setUpdatingProfileModal(true)
                TagManager.popupEvent({
                    page: 'verification_phone_otp',
                    field: `internal_error :: ${error.message}`,
                })
            } else {
                setShowModal(true)
                TagManager.popupEvent({ page: 'verification_phone_otp', field: 'incorrect_otp' })
            }
            reset({ otp: '' })
        }
    }

    return (
        <>
            {loading && <CoverSpinning />}
            {showModal && (
                <NoticeModal
                    title="รหัส OTP ไม่ถูกต้อง"
                    description={NoticeMessage['otp.not.valid']}
                    onClickButton={() => {
                        setShowModal(false)
                        clearErrors()
                    }}
                />
            )}
            {showTooManyResentModal && (
                <NoticeModal
                    title="ไม่สามารถดำเนินการได้"
                    description={NoticeMessage['too.many.request']}
                    onClickButton={() => {
                        setShowTooManyResentModal(false)
                        clearErrors()
                    }}
                />
            )}
            {updatingProfileModal && (
                <NoticeModal
                    title="ไม่สามารถดำเนินการได้"
                    description={NoticeMessage['internal.error']}
                    onClickButton={() => {
                        setUpdatingProfileModal(false)
                        clearErrors()
                    }}
                />
            )}
            <div className="verification-otp">
                <Header
                    title="ยืนยันตัวตน"
                    messages={
                        isKYCForCampaign
                            ? []
                            : [
                                  {
                                      type: 'text',
                                      text: NoticeMessage.ABORTED_AUTHEN_LINE_SHARING,
                                  },
                              ]
                    }
                />
                <div className="abcs-container">
                    <div className="otp-desc-reference">
                        <span>รหัส OTP ได้ส่งไปยังโทรศัพท์มือถือหมายเลข</span>
                        <span className="phone-info">
                            {Strings.mobileNumberMasked(
                                Strings.mobileNumberWithoutCode(appState.phoneNumber)
                            )}{' '}
                            (Ref.
                            {appState.reference.slice(0, 4).toUpperCase()})
                        </span>
                    </div>

                    <div className="input-container">
                        <div className={`abcs-input fluid ${classnames(errors.otp && 'error')}`}>
                            <label htmlFor="otp">กรอกรหัส OTP ที่ได้รับ</label>
                            <input
                                id="otp"
                                pattern="[0-9]*"
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                name={otpField.name}
                                onChange={otpField.onChange}
                                ref={otpField.ref}
                                onBlur={(e) => {
                                    otpField.onBlur(e)
                                    validate()
                                }}
                            />
                        </div>
                        {errors.otp && <small>{errors.otp.message}</small>}
                    </div>

                    <div className="resent">
                        <div
                            role="button"
                            tabIndex={0}
                            onClick={onClickResentOTP}
                            className={classnames('otp-link', { '--disabled': !hasExpired })}
                        >
                            ขอรหัส OTP อีกครั้ง
                        </div>
                        <span className="resent-info">
                            อีก{' '}
                            <Countdown
                                reset={!hasExpired}
                                duration={15 * 60}
                                startOn={15}
                                setHasExpired={setHasExpired}
                            />{' '}
                            นาทีจะขอได้อีกครั้ง
                        </span>
                    </div>

                    <div className="operation-button-container">
                        <button
                            type="button"
                            className="abcs-button primary"
                            onClick={handleSubmit(onSubmitSentOTP)}
                        >
                            ต่อไป
                        </button>
                        <button type="button" className="abcs-button" onClick={() => navigate(-1)}>
                            ย้อนกลับ
                        </button>
                    </div>
                </div>
            </div>
        </>
    )
}
